let _hasRumViewEverBeenBackgrounded = document.hidden;

type RumViewBackgroundedListener = () => void;
const backgroundedListeners = new Set<RumViewBackgroundedListener>();
// Source for "passive" = visible but unfocused:
// https://developer.chrome.com/docs/web-platform/page-lifecycle-api#states
// Local heuristic, not a browser/Datadog threshold: require passive to last at
// least 1s so short focus churn does not clutter page-state vitals.
const MIN_PASSIVE_PAGE_STATE_PERIOD_DURATION_MS = 1000;

/**
 * Page Lifecycle states we mirror from Chrome's definitions:
 * https://developer.chrome.com/docs/web-platform/page-lifecycle-api#states
 *
 * - active: visible + focused
 * - passive: visible + not focused
 * - hidden: not visible, and not frozen/discarded/terminated
 * - frozen: browser suspended freezable task queues
 * - terminated: unload/memory cleanup has begun
 * - discarded: documented state, but not observable when it happens
 *
 * Evidence for excluding `discarded` from this runtime type:
 * - Chrome says discarded runs no tasks, event callbacks, or JavaScript:
 *   https://developer.chrome.com/docs/web-platform/page-lifecycle-api#states
 * - Chrome says hidden/frozen can enter discarded with no events fired:
 *   https://developer.chrome.com/docs/web-platform/page-lifecycle-api#states
 * - Chrome says prior discard is detected at page load time via
 *   `document.wasDiscarded`, and discarded pages must be reloaded:
 *   https://developer.chrome.com/docs/web-platform/page-lifecycle-api#new_features_added_in_chrome_68
 */
export type RumViewPageState =
  | 'active'
  | 'passive'
  | 'hidden'
  | 'frozen'
  | 'terminated';

/**
 * These values are emitted as state-specific RUM duration vitals (`page_passive`,
 * `page_hidden`, or `page_frozen`) and as `context.pageState`; both map 1:1 to
 * the completed Chrome Page Lifecycle state period:
 * https://developer.chrome.com/docs/web-platform/page-lifecycle-api#states
 *
 * Local instrumentation choices:
 * - `active` is the baseline and is not emitted as a duration vital.
 * - `passive` is emitted only after MIN_PASSIVE_PAGE_STATE_PERIOD_DURATION_MS;
 *   this threshold is a noise-reduction heuristic, not a browser/Datadog rule.
 * - `hidden` and `frozen` are emitted without a minimum.
 *
 * Evidence for states that are intentionally not emitted:
 * - `terminated` is not reliably closeable; Chrome/MDN both recommend treating
 *   `hidden` as the last reliable lifecycle point, especially on mobile:
 *   https://developer.chrome.com/docs/web-platform/page-lifecycle-api#developer_recommendations_for_each_state
 *   https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilitychange_event
 * - `discarded` is not observable when it happens; see `RumViewPageState`.
 */
export type RumViewMeasuredPageState = Extract<
  RumViewPageState,
  'passive' | 'hidden' | 'frozen'
>;

/**
 * A completed measured page-state period of the current RUM view. `startMark`
 * is in performance.now() milliseconds (epoch time is
 * `performance.timeOrigin + startMark`).
 */
export type RumViewPageStatePeriod = {
  state: RumViewMeasuredPageState;
  startMark: number;
  durationMs: number;
};

type RumViewPageStatePeriodListener = (period: RumViewPageStatePeriod) => void;
const pageStatePeriodListeners = new Set<RumViewPageStatePeriodListener>();

let currentPageState = computeRumViewPageState();
// Start mark of the in-progress measured page-state period, if the current
// state is one we record. The RUM SDK backdates the first manually-tracked view
// to navigation start (preStartRum defers startup to the first `startView` call
// and trackViews anchors the initial view at `clocksOrigin()`), so if the page
// loaded hidden, that hidden period is counted from the performance epoch
// (navigation start) — not the moment `startDatadogRumView` first runs.
//
// Evidence in installed @datadog/browser-rum v7.5.0:
// - @datadog/browser-rum-core/src/domain/view/trackViews.ts initializes the
//   first view with `startNewView(..., clocksOrigin(), ...)`.
// - @datadog/js-core/src/entries/time.ts defines `clocksOrigin().relative` as 0
//   and its timestamp as the navigation time origin.
let pageStatePeriodStartMark: number | null =
  currentPageState === 'hidden' ? 0 : getPageStateStartMark(currentPageState);
let hasAnchoredInitialRumView = false;

/**
 * Registers a listener invoked when the current RUM view first becomes
 * backgrounded (the false → true transition of
 * `hasRumViewEverBeenBackgrounded`, re-armed by `resetRumViewBackgrounded` on
 * each new view).
 *
 * @returns An unsubscribe function
 */
export function onRumViewBackgrounded(
  listener: RumViewBackgroundedListener
): () => void {
  backgroundedListeners.add(listener);
  return () => {
    backgroundedListeners.delete(listener);
  };
}

/**
 * Registers a listener invoked with every completed measured page-state period
 * of the current RUM view. We record passive, hidden, and frozen periods;
 * active is the baseline, and terminated is a terminal transition rather than a
 * duration that can be closed reliably.
 *
 * @returns An unsubscribe function
 */
export function onRumViewPageStatePeriod(
  listener: RumViewPageStatePeriodListener
): () => void {
  pageStatePeriodListeners.add(listener);
  return () => {
    pageStatePeriodListeners.delete(listener);
  };
}

export function hasRumViewEverBeenBackgrounded() {
  return _hasRumViewEverBeenBackgrounded;
}

/**
 * Returns the current state maintained by the shared Page Lifecycle listeners.
 * Consumers should read this at the moment they need a snapshot rather than
 * registering duplicate focus and lifecycle listeners.
 */
export function getRumViewPageState(): RumViewPageState {
  return currentPageState;
}

export function resetRumViewBackgrounded() {
  _hasRumViewEverBeenBackgrounded = document.hidden;
}

/**
 * Re-anchors page-state period marks for a new RUM view (called by
 * `startDatadogRumView`). A measured period spanning a view change is
 * attributed to the new view from the view start onward, so it can't leak the
 * old view's time.
 */
export function resetRumViewVisibilityMarks() {
  // First view: keep the navigation-start anchor (see note above).
  if (!hasAnchoredInitialRumView) {
    hasAnchoredInitialRumView = true;
    return;
  }
  currentPageState = computeRumViewPageState();
  pageStatePeriodStartMark = getPageStateStartMark(currentPageState);
}

export function setRumViewBackgrounded() {
  if (_hasRumViewEverBeenBackgrounded) {
    return;
  }
  _hasRumViewEverBeenBackgrounded = true;
  backgroundedListeners.forEach(listener => listener());
}

// ---- Intentionally not exported ----
// Please think twice before exporting the state-transition functions below.

function isMeasuredPageState(
  state: RumViewPageState
): state is RumViewMeasuredPageState {
  return state === 'passive' || state === 'hidden' || state === 'frozen';
}

function getPageStateStartMark(state: RumViewPageState) {
  return isMeasuredPageState(state) ? performance.now() : null;
}

function shouldEmitPageStatePeriod(
  state: RumViewMeasuredPageState,
  durationMs: number
) {
  return (
    state !== 'passive' ||
    durationMs >= MIN_PASSIVE_PAGE_STATE_PERIOD_DURATION_MS
  );
}

function computeRumViewPageState(event?: Event): RumViewPageState {
  if (event?.type === 'freeze') {
    return 'frozen';
  }
  if (event?.type === 'pagehide') {
    return (event as PageTransitionEvent).persisted ? 'frozen' : 'terminated';
  }
  if (document.hidden || document.visibilityState === 'hidden') {
    return 'hidden';
  }
  if (document.hasFocus()) {
    return 'active';
  }
  return 'passive';
}

function updatePageStatePeriodMarks(nextState: RumViewPageState) {
  if (nextState === currentPageState) {
    return;
  }

  const now = performance.now();
  if (
    isMeasuredPageState(currentPageState) &&
    pageStatePeriodStartMark != null &&
    nextState !== 'terminated'
  ) {
    const durationMs = now - pageStatePeriodStartMark;
    const period: RumViewPageStatePeriod = {
      state: currentPageState,
      startMark: pageStatePeriodStartMark,
      durationMs,
    };
    if (shouldEmitPageStatePeriod(currentPageState, durationMs)) {
      pageStatePeriodListeners.forEach(listener => listener(period));
    }
  }

  currentPageState = nextState;
  pageStatePeriodStartMark = isMeasuredPageState(nextState) ? now : null;
}

function handlePageStateChange(event?: Event) {
  if (document.hidden) {
    setRumViewBackgrounded();
  }
  updatePageStatePeriodMarks(computeRumViewPageState(event));
}

const pageStateListenerOptions = {capture: true};
// Event targets follow the Page Lifecycle docs:
// https://developer.chrome.com/docs/web-platform/page-lifecycle-api#how_to_observe_state_changes
//
// MDN docs:
// - visibilitychange: https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilitychange_event
// - pageshow: https://developer.mozilla.org/en-US/docs/Web/API/Window/pageshow_event
// - pagehide: https://developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event
// - focus: https://developer.mozilla.org/en-US/docs/Web/API/Window/focus_event
// - blur: https://developer.mozilla.org/en-US/docs/Web/API/Window/blur_event
//
// Gotchas from the docs: `hidden` is often the last reliably observable state
// (especially on mobile), `pageshow` can fire for background/prerendered pages,
// and `pagehide` may be skipped on mobile. `freeze`/`resume` are currently
// missing in Firefox, Safari/iOS Safari, IE, and EdgeHTML-era Edge, so frozen
// periods are best-effort:
// https://caniuse.com/mdn-api_document_freeze_event
// https://caniuse.com/mdn-api_document_resume_event
const pageStateEventTargets: Array<{target?: EventTarget; events: string[]}> = [
  {
    target: typeof document !== 'undefined' ? document : undefined,
    events: ['visibilitychange', 'freeze', 'resume'],
  },
  {
    target: typeof window !== 'undefined' ? window : undefined,
    events: ['pageshow', 'pagehide', 'focus', 'blur'],
  },
];

pageStateEventTargets.forEach(({target, events}) => {
  events.forEach(eventName => {
    target?.addEventListener(
      eventName,
      handlePageStateChange,
      pageStateListenerOptions
    );
  });
});
